import {
	AUTH_TOKEN_COOKIE,
	BACKFILL_STATUS_COOKIE,
	sharedCookieOptions,
} from "@/constants";
import { clearGleapIdentity, identifyGleapUser } from "@jace/insights";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { getDisplayName } from "./get-display-name";
import { removeLegacyAuthToken } from "./migrations";
import type {
	AuthContextType,
	DecodedToken,
	GetTokenCallable,
	User,
} from "./types";

const AuthContext = createContext<AuthContextType>({
	isSignedIn: false,
	userId: undefined,
	user: null,
	signIn: () => undefined,
	signOut: () => undefined,
	// signUp: () => undefined,
	loading: true,
	getToken: async () => null,
	token: null,
});

interface AuthData {
	user: User | null;
	token: string | null;
	loading: boolean;
}

export function AuthProvider({
	children,
}: {
	children: React.ReactNode;
}): React.ReactElement | null {
	const [data, setData] = useState<AuthData>({
		user: null,
		token: null,
		loading: true,
	});

	const posthog = usePostHog();

	useEffect(() => {
		function loadUserFromCookies(): void {
			let user = null;
			const cookieToken = Cookies.get(AUTH_TOKEN_COOKIE) ?? null;

			if (cookieToken) {
				const decoded = jwtDecode<DecodedToken>(cookieToken);
				if (decoded.exp > Date.now() / 1000) {
					const email = decoded.email;
					const firstName = decoded.first_name;
					const lastName = decoded.last_name;
					const registeredAt = decoded.user_created_at;
					const displayName =
						decoded.display_name ||
						getDisplayName({ email, firstName, lastName });
					user = {
						id: decoded.user_id,
						imageUrl: decoded.image_url,
						email,
						firstName,
						lastName,
						displayName,
						registeredAt,
					};

					// Identify user in Gleap
					identifyGleapUser(user.id, {
						name: displayName,
						email: email,
						createdAt: new Date(registeredAt),
						customData: {
							firstName,
							lastName,
							imageUrl: decoded.image_url,
						},
					});
				}
			}

			setData({
				user,
				token: cookieToken,
				loading: false,
			});
		}

		loadUserFromCookies();
	}, []);

	useEffect(() => {
		// Ensure PostHog is initialized before identifying user
		if (data.user && posthog?.__loaded) {
			console.debug("identify user in posthog", data.user.id);
			posthog.identify(data.user.id, {
				email: data.user.email,
				first_name: data.user.firstName,
				last_name: data.user.lastName,
			});
		}
	}, [data.user, posthog]);

	const signIn = useCallback(
		(redirectUrl = ""): void => {
			const searchParams = new URLSearchParams();

			// Add redirect_url directly
			searchParams.set("redirect_url", redirectUrl);

			if (posthog?.get_session_id) {
				const posthogSessionId = posthog.get_session_id();
				if (posthogSessionId) {
					// Add it as a separate state parameter - this gets stored in session
					searchParams.set("ph_session_id", posthogSessionId);
				}
			}

			const currentSearchParams = new URLSearchParams(window.location.search);
			if (currentSearchParams.has("test_user")) {
				const testUser = currentSearchParams.get("test_user");
				if (testUser) {
					searchParams.set("test_user", testUser);
				}
			}
			window.location.assign(
				`${process.env.NEXT_PUBLIC_API_URL}/oauth/google?${searchParams.toString()}`,
			);
		},
		[posthog],
	);

	// const signUp = useCallback(
	// 	(redirectUrl = ""): void => {
	// 		const searchParams = new URLSearchParams();
	// 		searchParams.set("redirect_url", redirectUrl);

	// 		if (posthog?.get_session_id) {
	// 			const posthogSessionId = posthog.get_session_id();
	// 			console.log("posthog session id signup", posthogSessionId);
	// 			if (posthogSessionId) {
	// 				searchParams.set("posthog_session_id", posthogSessionId);
	// 			}
	// 		}

	// 		const currentSearchParams = new URLSearchParams(window.location.search);
	// 		if (currentSearchParams.has("test_user")) {
	// 			searchParams.set("test_user", currentSearchParams.get("test_user")!);
	// 		}

	// 		console.log(
	// 			"signup",
	// 			`${process.env.NEXT_PUBLIC_API_URL}/oauth/google?${searchParams.toString()}`,
	// 		);
	// 		window.location.assign(
	// 			`${process.env.NEXT_PUBLIC_API_URL}/oauth/google?${searchParams.toString()}`,
	// 		);
	// 	},
	// 	[posthog],
	// );

	const signOut = useCallback(
		(redirectUrl = "/signin"): void => {
			removeLegacyAuthToken();
			posthog.reset();
			Cookies.remove(AUTH_TOKEN_COOKIE, sharedCookieOptions);

			Cookies.remove(BACKFILL_STATUS_COOKIE);
			clearGleapIdentity();
			setData((prev) => ({
				...prev,
				user: null,
			}));
			window.location.replace(redirectUrl);
		},
		[posthog],
	);

	const getToken: GetTokenCallable = useCallback(async () => {
		if (!data.token) {
			return null;
		}
		const decoded = jwtDecode<DecodedToken>(data.token);
		if (Date.now() / 1000 >= decoded.exp) {
			removeLegacyAuthToken();
			Cookies.remove(AUTH_TOKEN_COOKIE, sharedCookieOptions);
			Cookies.remove(BACKFILL_STATUS_COOKIE);

			setData((prev) => ({
				...prev,
				token: null,
				user: null,
			}));

			return null;
		}
		return data.token;
	}, [data.token]);

	if (data.loading) {
		return null;
	}

	return (
		<AuthContext.Provider
			value={{
				isSignedIn: Boolean(data.user),
				userId: data.user ? data.user.id : undefined,
				user: data.user,
				token: data.token,
				loading: data.loading,
				signIn,
				// signUp,
				signOut,
				getToken,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth(): AuthContextType {
	const ctx = useContext(AuthContext);

	if (!ctx) {
		throw new Error("useAuth must be used within a AuthProvider");
	}

	return ctx;
}
